import { FC, useEffect, useRef, useState } from "react";
import { ValidatedInputProps, InputCommonProps } from "./Common";
import { useForceUpdate } from "../../Hooks";
import commonStyles from "./Common.module.scss";
import styles from "./SleekInput.module.scss";
import { classNames } from "../../Utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

interface SleekInputProps {
    autoFocus?: boolean;
}

export type CombinedInputProps = SleekInputProps & ValidatedInputProps & InputCommonProps & React.InputHTMLAttributes<HTMLInputElement>;

export const SleekInput: FC<CombinedInputProps> = (props) => {
    const {
        validator,
        validations,
        customMessages,
        label,
        className,
        autoFocus,
        allowShowPassword,
        type,
        ...inputProps
    } = props;

    const forceUpdate = useForceUpdate();
    const inputRef = useRef<HTMLInputElement>(null);
    const [showPassword, setShowPassword] = useState(false);

    const canToggleShowPassword = type === "password" && allowShowPassword;
    const resolvedType = canToggleShowPassword && showPassword ? "text" : type;

    useEffect(() => {
        validator?.register({
            key: props.id,
            validations: validations,
            value: props.value?.toString(),
            forceUpdate: forceUpdate,
            validationMessages: customMessages
        });

        if (autoFocus) inputRef?.current?.focus();

        return () => {
            validator?.unregister(props.id);
        };
    }, []);

    useEffect(() => {
        if (validator) {
            validator.setValue(props.id, props.value?.toString() ?? null);
            forceUpdate();
        }
    }, [props.value]);

    const isErrorState = (): boolean => {
        return !!validator && validator.submitAttempted() && validator.getErrors(props.id).length > 0;
    };

    return (
        <div className={commonStyles.container}>
            <label
                htmlFor={props.id}
                data-required={props.validations?.required}
                className={classNames([
                    styles.label,
                    isErrorState() && styles.invalid,
                    props.label == null && styles.noLabel
                ])}
            >
                <span>{props.label}</span>
                <input
                    ref={inputRef}
                    type={resolvedType}
                    placeholder={props.placeholder || " "}
                    {...inputProps}
                />
                {canToggleShowPassword && (
                    <FontAwesomeIcon
                        className={styles.showPasswordIcon}
                        icon={showPassword ? faEyeSlash : faEye}
                        onClick={() => setShowPassword((prev) => !prev)}
                    />
                )}
            </label>

            {isErrorState() && (
                <div className={commonStyles.validation}>
                    {validator?.getErrors(props.id).map((message) => {
                        return <div key={message}>{message}</div>;
                    })}
                </div>
            )}
        </div>
    );
};